import {
  Component,
  ElementRef,
  ViewChild,
  AfterViewInit,
  OnInit,
  OnDestroy,
  NgZone,
  inject,
  signal,
  effect
} from '@angular/core';
import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry.js';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';

@Component({
  selector: 'app-three-html-canvas',
  standalone: true,
  template: `
    <div class="three-container" #rendererContainer></div>
    <div class="info-badge">
      <button (click)="changeSvgIndex()">
        <i class="bi bi-arrow-clockwise"></i>
      </button>
    </div>
  `,
  styles: [`
    :host {
      display: block;
      width: 100%;
      height: 100%;
      position: relative;
    }

    .three-container {
      width: 100%;
      height: 100%;
      min-width: 100px;
      min-height: 100px;
      overflow: hidden;
    }

    .three-container ::ng-deep canvas {
      display: block;
      width: 100% !important;
      height: 100% !important;
    }

    .info-badge {
      position: absolute;
      top: 10px;
      right: 10px;
      color: #fff;
      padding: 8px 12px;
      border-radius: 4px;
      z-index: 10;
      display: flex;
      gap: 10px;
      align-items: center;
    }

    button {
      background: #17459aff;
      color: white;
      border: none;
      padding: 4px 8px;
      border-radius: 4px;
      cursor: pointer;
      transition: background 0.2s;
    }
    button:hover {
      background: #1d4ed8;
    }
  `]
})
export class ThreeHtmlCanvasComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('rendererContainer', { static: true })
  private containerRef!: ElementRef<HTMLDivElement>;

  private ngZone = inject(NgZone);

  readonly imageSrc = signal<string>('/www1.svg');
  private svgIndex = 1;

  private camera!: THREE.PerspectiveCamera;
  private scene!: THREE.Scene;
  private renderer!: THREE.WebGLRenderer;
  private mesh!: THREE.Mesh;
  private textureLoader = new THREE.TextureLoader();
  private material!: THREE.MeshStandardMaterial;
  private pmremGenerator?: THREE.PMREMGenerator;
  private resizeObserver?: ResizeObserver;

  constructor() {
    effect(() => {
      const src = this.imageSrc();
      if (this.material) {
        this.updateTextureFromUrl(src);
      }
    });
  }

  ngOnInit(): void {
    const today = new Date().getDay();
    this.svgIndex = today === 0 ? 7 : today;
    this.imageSrc.set(`/www${this.svgIndex}.svg`);
  }

  ngAfterViewInit(): void {
    this.ngZone.runOutsideAngular(() => {
      this.initThree();
      this.setupResizeObserver();
    });
  }

  ngOnDestroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.pmremGenerator) {
      this.pmremGenerator.dispose();
    }
    if (this.renderer) {
      this.renderer.setAnimationLoop(null);
      this.renderer.dispose();
      this.renderer.domElement.remove();
    }
  }

  private initThree(): void {
    const container = this.containerRef.nativeElement;
    const width = container.clientWidth || 300;
    const height = container.clientHeight || 300;

    // 1. Configuración avanzada del WebGLRenderer
    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.3;
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(width, height, false);
    container.appendChild(this.renderer.domElement);

    // 2. Cámara y Escena
    this.camera = new THREE.PerspectiveCamera(50, width / height, 1, 2000);
    this.camera.position.z = 395;

    this.scene = new THREE.Scene();

    this.pmremGenerator = new THREE.PMREMGenerator(this.renderer);
    const roomEnv = new RoomEnvironment();
    const envTexture = this.pmremGenerator.fromScene(roomEnv, 0.03).texture;
    this.scene.environment = envTexture;

    // Sistema de Iluminación
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
    this.scene.add(ambientLight);

    const mainLight = new THREE.DirectionalLight(0x38bdf8, 3.5);
    mainLight.position.set(300, 400, 500);
    this.scene.add(mainLight);

    const rimLight = new THREE.DirectionalLight(0x818cf8, 2.5);
    rimLight.position.set(-300, -200, -300);
    this.scene.add(rimLight);

    const pointLight = new THREE.PointLight(0xffffff, 2, 800);
    pointLight.position.set(0, 0, 400);
    this.scene.add(pointLight);

    // 3. Geometría y Material PBR optimizado
    const geometry = new RoundedBoxGeometry(200, 200, 200, 12, 12);
    this.material = new THREE.MeshStandardMaterial({
      roughness: 0.15,
      metalness: 0.25,
      envMapIntensity: 1.8
    });

    this.mesh = new THREE.Mesh(geometry, this.material);
    this.scene.add(this.mesh);

    // 4. Cargar Textura
    this.updateTextureFromUrl(this.imageSrc());

    // 5. Iniciar Render Loop
    this.renderer.setAnimationLoop((time: number) => this.animate(time));
  }

  private setupResizeObserver(): void {
    const container = this.containerRef.nativeElement;

    this.resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        if (width > 0 && height > 0) {
          this.resizeRendererToContainer(width, height);
        }
      }
    });

    this.resizeObserver.observe(container);
  }

  private resizeRendererToContainer(width: number, height: number): void {
    if (!this.renderer || !this.camera) return;

    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height, false);
  }

  private updateTextureFromUrl(url: string): void {
    this.textureLoader.load(
      url,
      (texture) => {
        texture.colorSpace = THREE.SRGBColorSpace;
        
        // Configuración para centrar, escalar (margen interno) y corregir inversión
        texture.wrapS = THREE.RepeatWrapping;
        texture.wrapT = THREE.RepeatWrapping;
        
        // Factor de escala (ej. 1.3 para alejar la imagen y dejar un margen limpio alrededor)
        const scale = 1.3; 
        texture.repeat.set(-scale, -scale);
        
        // Centrar la textura compensando la escala y la inversión
        texture.offset.set((1 + scale) / 2, (1 + scale) / 2);

        if (this.material.map) this.material.map.dispose();
        this.material.map = texture;
        this.material.needsUpdate = true;
      },
      undefined,
      () => {
        this.loadFallbackSvgData();
      }
    );
  }

  private loadFallbackSvgData(): void {
    const svgString = `
      <svg xmlns="http://www.w3.org/2000/svg" width="512" height="512">
        <rect width="100%" height="100%" fill="transparent"/>
        <text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-size="32" fill="#2563eb" font-family="sans-serif">
          Contenido Adaptado
        </text>
      </svg>
    `;

    const encodedSvg = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgString);
    this.textureLoader.load(encodedSvg, (texture) => {
      texture.colorSpace = THREE.SRGBColorSpace;
      texture.wrapS = THREE.RepeatWrapping;
      texture.wrapT = THREE.RepeatWrapping;
      
      const scale = 1.3;
      texture.repeat.set(-scale, -scale);
      texture.offset.set((1 + scale) / 2, (1 + scale) / 2);

      this.material.map = texture;
      this.material.needsUpdate = true;
    });
  }

  public changeSvgIndex(): void {
    this.svgIndex = this.svgIndex >= 7 ? 1 : this.svgIndex + 1;
    this.imageSrc.set(`/www${this.svgIndex}.svg`);
  }

  private animate = (time: number): void => {
    if (this.mesh) {
      this.mesh.rotation.x = Math.sin(time * 0.0005) * 0.5;
      this.mesh.rotation.y = Math.cos(time * 0.0008) * 0.5;
    }
    this.renderer.render(this.scene, this.camera);
  };
}